import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { candidateAPI } from "../services/api";
import "./BulkUpload.css";

const ACCEPTED = ".csv,.xlsx,.xls";
const STATUS_FILTERS = [
  { id: "actionable", label: "Needs attention" },
  { id: "all", label: "All rows" },
];

const BulkUpload = () => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("actionable");

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    setReport(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setReport(null);

    try {
      const response = await candidateAPI.bulkRegister(file);
      setReport(response);
      const { registered, failed } = response.summary;
      if (failed > 0) {
        toast.warning(`${registered} registered, ${failed} need attention.`);
      } else {
        toast.success(`${registered} candidates registered.`);
      }
    } catch (error) {
      const message = error?.message || "Upload failed. Please check the file and try again.";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const visibleResults = useMemo(() => {
    if (!report) return [];
    if (filter === "all") return report.results;
    return report.results.filter((r) => r.status !== "registered");
  }, [report, filter]);

  const downloadSample = () => {
    const header = "FILE NO.,RegNum,NAME,emailID,SEX";
    const rows = ["1001,1001NE,Mr. John A. Doe,1001NE@neco.gov.ng,M", "1002,1002NE,Mrs. Jane B. Smith,1002NE@neco.gov.ng,F"];
    const blob = new Blob([`${header}\n${rows.join("\n")}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk-register-sample.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadErrors = () => {
    if (!report) return;
    const rows = report.results.filter((r) => r.status === "failed");
    if (rows.length === 0) return;
    const header = "row,regNo,email,reason";
    const body = rows
      .map((r) => [r.row, r.regNo || "", r.email || "", `"${(r.reason || "").replace(/"/g, '""')}"`].join(","))
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk-register-errors.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const summary = report?.summary;
  const hasFailures = summary?.failed > 0;

  return (
    <div className="bulk-container">
      <div className="bulk-card">
        <div className="bulk-header">
          <h2>Bulk Candidate Registration</h2>
          <p>Upload an Excel (.xlsx) or CSV file to register multiple candidates at once.</p>
        </div>

        <div className="bulk-body">
        <div className="bulk-upload-zone">
          <label htmlFor="bulk-file" className="bulk-file-label">
            <span className="bulk-file-cta">{file ? "Change file" : "Choose a file"}</span>
            <span className="bulk-file-name">{file ? file.name : "No file selected"}</span>
            <input id="bulk-file" type="file" accept={ACCEPTED} onChange={handleFileChange} disabled={isUploading} />
          </label>

          <button type="button" className="bulk-upload-btn" onClick={handleUpload} disabled={!file || isUploading}>
            {isUploading ? "Registering..." : "Upload & Register"}
          </button>
        </div>

        <p className="bulk-help">
          Required columns: <code>FILE NO.</code>, <code>RegNum</code>, <code>NAME</code>, <code>emailID</code>, <code>SEX</code>. Confirmation emails and syncing run in the background after upload. If an upload is interrupted, re-upload the same file &mdash; completed candidates are skipped automatically.
        </p>

        <button type="button" className="bulk-sample-link" onClick={downloadSample}>
          Download sample file
        </button>

        {isUploading && (
          <div className="bulk-loading">
            <div className="bulk-spinner" />
            <span>Registering candidates. This can take a moment for large files...</span>
          </div>
        )}

        {summary && (
          <div className="bulk-results">
            <div className="bulk-stats">
              <div className="bulk-stat">
                <span className="bulk-stat-value">{summary.total}</span>
                <span className="bulk-stat-label">Total rows</span>
              </div>
              <div className="bulk-stat bulk-stat--ok">
                <span className="bulk-stat-value">{summary.registered}</span>
                <span className="bulk-stat-label">Registered</span>
              </div>
              <div className="bulk-stat bulk-stat--muted">
                <span className="bulk-stat-value">{summary.skipped}</span>
                <span className="bulk-stat-label">Skipped</span>
              </div>
              <div className={`bulk-stat ${hasFailures ? "bulk-stat--error" : ""}`}>
                <span className="bulk-stat-value">{summary.failed}</span>
                <span className="bulk-stat-label">Failed</span>
              </div>
            </div>

            <div className="bulk-banner">
              Confirmation emails, photocards and syncing are now processing in the background. You can close this page; they will continue.
            </div>

            <div className="bulk-toolbar">
              <div className="bulk-filters">
                {STATUS_FILTERS.map((f) => (
                  <button key={f.id} type="button" className={`bulk-filter ${filter === f.id ? "active" : ""}`} onClick={() => setFilter(f.id)}>
                    {f.label}
                  </button>
                ))}
              </div>
              {hasFailures && (
                <button type="button" className="bulk-download" onClick={downloadErrors}>
                  Download failed rows (CSV)
                </button>
              )}
            </div>

            {visibleResults.length === 0 ? (
              <div className="bulk-empty">Nothing to show here. Everything was registered successfully.</div>
            ) : (
              <div className="bulk-table-wrap">
                <table className="bulk-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Reg No</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleResults.map((r, idx) => (
                      <tr key={`${r.row}-${idx}`}>
                        <td>{r.row}</td>
                        <td>{r.regNo || "-"}</td>
                        <td>{r.email || "-"}</td>
                        <td>
                          <span className={`bulk-badge bulk-badge--${r.status}`}>{r.status}</span>
                        </td>
                        <td>{r.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
